import * as fs from 'fs';
import * as path from 'path';
import {
  detectFramework,
  isAwsDeployTarget,
  nextConfigHasStaticExport,
  nextConfigIncludesTranspilePackages,
  resolveSdkPackageName,
  sourceHasUseClientDirective,
  viteConfigHasRelativeBase,
  type AgentFramework,
} from './detect-framework';

export interface ScaffoldCheckResult {
  ok: boolean;
  framework: AgentFramework;
  errors: string[];
  warnings: string[];
}

function readPackageJson(projectRoot: string): Record<string, unknown> | null {
  const pkgPath = path.join(projectRoot, 'package.json');
  if (!fs.existsSync(pkgPath)) return null;
  try {
    return JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function findFirstExisting(projectRoot: string, candidates: string[]): string | null {
  for (const rel of candidates) {
    const abs = path.join(projectRoot, rel);
    if (fs.existsSync(abs)) return rel;
  }
  return null;
}

function readTextFile(projectRoot: string, rel: string): string {
  return fs.readFileSync(path.join(projectRoot, rel), 'utf8');
}

function isAwsDeploy(projectRoot: string): boolean {
  if (process.env.CFM_DEPLOY_TARGET === 'aws') return true;
  const candidates = ['survey-deploy-config.json', 'survey-ui-config.json'];
  for (const name of candidates) {
    const configPath = path.join(projectRoot, name);
    if (!fs.existsSync(configPath)) continue;
    try {
      const raw = JSON.parse(fs.readFileSync(configPath, 'utf8')) as { deploy?: { target?: string } };
      return isAwsDeployTarget(raw);
    } catch {
      return false;
    }
  }
  return false;
}

function globalsHasTailwindImport(projectRoot: string): boolean {
  const globalsCandidates = [
    'src/app/globals.css',
    'src/styles/globals.css',
    'src/index.css',
    'app/globals.css',
  ];
  for (const rel of globalsCandidates) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;
    const content = fs.readFileSync(abs, 'utf8');
    if (/@import\s+["']tailwindcss["']/.test(content)) return true;
  }
  return false;
}

export function runScaffoldValidation(projectRoot: string): ScaffoldCheckResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const pkg = readPackageJson(projectRoot);
  if (!pkg) {
    return {
      ok: false,
      framework: 'unknown',
      errors: ['package.json missing or invalid — cannot verify framework scaffold'],
      warnings: [],
    };
  }

  const deps = {
    ...(pkg.dependencies as Record<string, string> | undefined),
    ...(pkg.devDependencies as Record<string, string> | undefined),
  };

  const framework = detectFramework({
    dependencies: pkg.dependencies as Record<string, string> | undefined,
    devDependencies: pkg.devDependencies as Record<string, string> | undefined,
  });

  const sdkPkg = resolveSdkPackageName({
    dependencies: pkg.dependencies as Record<string, string> | undefined,
    devDependencies: pkg.devDependencies as Record<string, string> | undefined,
  });

  if (!sdkPkg) {
    errors.push(
      'Install @explorer02/cfm-survey-sdk (or @repo/sdk) — see setup/install.md',
    );
  }
  if (!deps.react) {
    errors.push('Install react — see setup/install.md');
  }
  if (!deps['@tanstack/react-query']) {
    errors.push('Install @tanstack/react-query — see setup/install.md');
  }
  if (!deps['react-icons']) {
    errors.push('Install react-icons — see setup/install.md');
  }

  if (framework === 'unknown') {
    warnings.push('Framework not detected from package.json — add next, vite, or react-scripts');
  }

  const awsDeploy = isAwsDeploy(projectRoot);
  const sdkPkgName = sdkPkg ?? '@explorer02/cfm-survey-sdk';

  if (framework === 'nextjs') {
    const nextConfig = findFirstExisting(projectRoot, [
      'next.config.js',
      'next.config.mjs',
      'next.config.ts',
    ]);
    if (!nextConfig) {
      errors.push(
        `Create next.config.js with transpilePackages: ['${sdkPkgName}'] — see setup/nextjs.md`,
      );
    } else {
      const source = readTextFile(projectRoot, nextConfig);
      if (!nextConfigIncludesTranspilePackages(source, sdkPkgName)) {
        errors.push(`Add transpilePackages: ['${sdkPkgName}'] to ${nextConfig}`);
      }
      if (awsDeploy) {
        if (!nextConfigHasStaticExport(source)) {
          errors.push(
            `Add output: 'export' and assetPrefix: './' to ${nextConfig} for AWS deploy — see setup/nextjs-aws-export.md`,
          );
        }
      }
    }

    const surveyPage = findFirstExisting(projectRoot, [
      'src/components/SurveyPage.tsx',
      'components/SurveyPage.tsx',
      'app/components/SurveyPage.tsx',
    ]);
    if (surveyPage) {
      const source = readTextFile(projectRoot, surveyPage);
      if (!sourceHasUseClientDirective(source)) {
        warnings.push(
          `Add 'use client' to ${surveyPage} — required for Next.js App Router`,
        );
      }
    }
  }

  if (framework === 'vite') {
    const viteConfig = findFirstExisting(projectRoot, [
      'vite.config.ts',
      'vite.config.js',
      'vite.config.mjs',
    ]);
    if (awsDeploy) {
      if (!viteConfig) {
        errors.push('Create vite.config.ts with base: \'./\' for AWS deploy — see setup/vite.md');
      } else {
        const source = readTextFile(projectRoot, viteConfig);
        if (!viteConfigHasRelativeBase(source)) {
          errors.push(`Add base: './' to ${viteConfig} for AWS deploy — see setup/vite.md`);
        }
      }
    }

    for (const rel of ['tsconfig.app.json', 'tsconfig.node.json'] as const) {
      const abs = path.join(projectRoot, rel);
      if (!fs.existsSync(abs)) continue;
      try {
        const raw = JSON.parse(fs.readFileSync(abs, 'utf8')) as {
          compilerOptions?: { ignoreDeprecations?: string };
        };
        const value = raw.compilerOptions?.ignoreDeprecations;
        if (value !== undefined && value !== '5.0') {
          warnings.push(
            `${rel}: ignoreDeprecations "${value}" is not IDE-safe — use "5.0" (or omit). Fix: agent-troubleshooting.md · re-run npx cfm-sdk seed-phase5`,
          );
        }
      } catch {
        /* invalid JSON — leave to tsc */
      }
    }
  }

  if (!fs.existsSync(path.join(projectRoot, 'survey-deploy-config.json'))
    && !fs.existsSync(path.join(projectRoot, 'survey-ui-config.json'))) {
    warnings.push('survey-deploy-config.json not found — expected after Phase 5 seed or before Step 7');
  }

  if (!globalsHasTailwindImport(projectRoot)) {
    warnings.push(
      'globals.css missing @import "tailwindcss" — required for Tailwind utility classes in survey UI',
    );
  }

  const introPage = findFirstExisting(projectRoot, [
    'src/components/IntroPage.tsx',
    'components/IntroPage.tsx',
    'app/components/IntroPage.tsx',
  ]);
  if (!introPage) {
    warnings.push(
      'IntroPage not found — author an intro page with a Start button that dispatches onAction START; see build-gate/intro-start-gate.md',
    );
  }

  return {
    ok: errors.length === 0,
    framework,
    errors,
    warnings,
  };
}
